import { computeDubCoverage } from '@src/editor/dubbing/DubCoverageService';
import { readOriginalVideo } from '@src/editor/dubbing/readOriginalVideo';
import { VideoElement } from '@src/editor/models/elements/VideoElement';
import { useDatoClient, useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useRef, useState } from 'react';

import type { DubCoverage } from '@src/editor/dubbing/DubCoverageService';
import type { VideoEditor } from '@src/editor/VideoEditor';
import type { ResolvedDubManifest } from '@src/repositories/DubbingRepository';

const logger = LogManager.getLogger('component:useDubCoverage');

/**
 * The editor holder, narrowed to the one field the sum reads. Both surfaces'
 * stores fit it: the clip preview's own editor store and Creator Studio's
 * (PLAT-11555). It is the HOLDER rather than the editor because neither store
 * publishes its editor as an observable, so the value is read at the moment of
 * use rather than depended on.
 */
export interface DubCoverageEditorHolder {
  editor: Pick<VideoEditor, 'timeline'> | null | undefined;
}

/**
 * How much of this timeline's speech has a dubbed voice.
 *
 * Gathers what the coverage sum needs and hands it to the pure service, which
 * owns every rule. Nothing is decided here:
 *
 * - the kept spans, from the live editor — the same read the dub placement
 *   does, so the two always describe the same picture;
 * - the saved translation's parent phrases for the language in question;
 * - the transcript, because a translation phrase carries no time of its own,
 *   read through the same accessor the translation itself uses so the two never
 *   disagree about which element a phrase names;
 * - the dub manifest for that language.
 *
 * A language it cannot answer for yet — nothing loaded, or the read still in
 * flight — simply has no entry, so a caller can render nothing without testing
 * several things itself.
 *
 * Scope is the caller's, not this hook's: it measures whatever the editor it is
 * given has kept. The clip preview hands it a clip-scoped editor and gets the
 * clip's gaps; Creator Studio hands it the story editor and gets the reel's.
 */
export function useDubCoverage({
  editorStore,
  isOriginalVideoElement,
  languages,
  refreshToken = 0,
}: {
  editorStore: DubCoverageEditorHolder;
  /** "Is this the picture's original video?" — which elements the spans come from. */
  isOriginalVideoElement: (probe: unknown) => boolean;
  /**
   * The languages to measure. Each carries its own dub, so each is answered
   * independently.
   */
  languages: readonly (string | null)[];
  /**
   * Bump to re-read after something has changed the dub itself. The manifest
   * lives on the server, so nothing this hook observes changes when a dub run
   * finishes, and the warning would keep describing the gap it just filled.
   */
  refreshToken?: number;
}): Record<string, DubCoverage> {
  const datoClientStore = useDatoClient();
  const { translationStore, transcriptionStore } = useStore();
  const videoCreator = useVideoCreatorStore();
  const storyId = videoCreator.storyId ?? null;
  const [coverage, setCoverage] = useState<Record<string, DubCoverage>>({});

  // Held in a ref and read at the moment of use, so a host passing a fresh
  // arrow every render does not restart the read on every render.
  const probeRef = useRef(isOriginalVideoElement);
  probeRef.current = isOriginalVideoElement;

  // A stable key, so a caller passing a fresh array literal every render does
  // not re-fire the read.
  const wanted = languages.filter((l): l is string => !!l);
  const languageKey = [...new Set(wanted)].sort().join(',');
  // The transcript elements are built fresh on every call, so they can never be
  // a dependency: an effect listing them re-fires every render, and one that
  // then sets state spins an async loop React's depth guard cannot catch. They
  // are read inside the effect, and this wholesale-replaced observable is what
  // re-triggers it.
  const transcriptRevision = transcriptionStore.originalTranscription;
  // PLAT-11555 — the kept picture, as a value that changes when it does.
  // Creator Studio's editor store lives for the whole session and
  // `transcriptRevision` only moves on a transcript reload, so without this a
  // section restored after the dub was made never re-measures: the notice keeps
  // stating the seconds it read at mount and the repair keeps sending the phrase
  // list built then. The clip preview escaped it only because its editor store
  // is replaced per clip.
  //
  // Top-level video elements only — nothing else yields a span, so nudging an
  // overlay must not spend a manifest fetch. The probe is deliberately not
  // applied: narrowing to the ORIGINAL video costs the composition walk
  // `readOriginalVideo` does, and counting a B-roll element only over-measures.
  // A render is what brings this read around, and the host is what makes one
  // happen (Creator Studio's panel observes `editorStore.tracksMap`).
  const spanRevision = (editorStore.editor?.timeline.allElements ?? [])
    .filter(
      (element): element is VideoElement => element instanceof VideoElement,
    )
    .map(
      element =>
        `${element.id}@${element.time}+${element.durationSeconds}:${element.getMediaStartTime()}`,
    )
    .join('|');

  useEffect(() => {
    const codes = languageKey ? languageKey.split(',') : [];
    const elements = transcriptionStore.getFinalTranscriptionElements();
    if (!storyId || codes.length === 0 || elements.length === 0) {
      setCoverage({});
      return undefined;
    }
    const editor = editorStore.editor;
    if (!editor) {
      setCoverage({});
      return undefined;
    }

    const controller = new AbortController();

    // Read the spans BEFORE awaiting: they come off the live editor, and the
    // picture can be edited while the manifest is in flight. Reading them after
    // would measure one cut against another cut's dub.
    const { spans } = readOriginalVideo(editor.timeline, probe =>
      probeRef.current(probe),
    );

    void Promise.all(
      codes.map(async (language): Promise<[string, DubCoverage] | null> => {
        // The store only holds a translation once something has opened it, so a
        // cache miss fetches rather than reporting "nothing to warn about": a
        // silent miss would hide a real gap.
        let phrases = translationStore.getTranslation(language);
        if (!phrases) {
          try {
            phrases =
              await datoClientStore.translationRepository.fetchTranslationByLanguage(
                storyId,
                language,
              );
          } catch (error: unknown) {
            logger.warn('Could not read the translation for coverage', {
              storyId,
              language,
              error: error instanceof Error ? error.message : String(error),
            });
            return null;
          }
        }
        // A language with no saved translation has no lines to be missing, so
        // it has nothing to warn about. Not an error.
        if (!phrases) return null;
        if (controller.signal.aborted) return null;
        let resolved: ResolvedDubManifest | null = null;
        try {
          resolved = await datoClientStore.dubbingRepository.getDubManifest({
            storyId,
            language,
            signal: controller.signal,
          });
        } catch (error: unknown) {
          if (controller.signal.aborted) return null;
          logger.warn('Could not read the dub manifest for coverage', {
            storyId,
            language,
            error: error instanceof Error ? error.message : String(error),
          });
          return null;
        }
        const result = computeDubCoverage({
          spans,
          phrases: phrases.parentPhrases ?? [],
          // The fragments, not the segments. A line's current words are its live
          // fragments joined; the segment's own stored value is the text from
          // before the line was split and no edit ever updates it.
          fragments: phrases.phrases ?? [],
          elements,
          // No manifest means nothing is dubbed, which is the state every
          // story starts in, not a failure.
          manifest: resolved?.manifest ?? null,
        });
        if (!result.ok) {
          logger.warn('Could not work out the dub coverage', {
            storyId,
            language,
            error: result.error.message,
          });
          return null;
        }
        if (result.value.unlocatable.length > 0) {
          // A line nothing can place is a line the sum does not account for,
          // and a hidden gap is the failure that matters here.
          logger.warn(
            'Some translated lines could not be placed on the timeline',
            {
              storyId,
              language,
              count: result.value.unlocatable.length,
            },
          );
        }
        return [language, result.value];
      }),
    ).then(entries => {
      if (controller.signal.aborted) return;
      setCoverage(
        Object.fromEntries(entries.filter(Boolean) as [string, DubCoverage][]),
      );
    });

    return () => controller.abort();
  }, [
    datoClientStore,
    editorStore,
    languageKey,
    refreshToken,
    spanRevision,
    storyId,
    translationStore,
    transcriptionStore,
    transcriptRevision,
  ]);

  return coverage;
}
